import { useFormContext } from 'react-hook-form';
import { TOption, getNestedErrorProperty } from '@lib/util';
import { InputHTMLAttributes, ReactNode } from 'react';
import clsx from 'clsx';
import { IcoClose } from '../icons/ico';

type CustomTOption = TOption & { additionalNode?: ReactNode };

type Props = {
  disabled?: boolean;
  name: string;
  required?: boolean;
  onBlur?: () => void;
  options: CustomTOption[];
  title: string;
  className?: string;
  optionsClassName?: string;
};

export const RadioComponent = ({ name, disabled, onBlur, required, options, title, className, optionsClassName }: Props) => {
  const {
    register,
    formState: { errors, dirtyFields },
    resetField,
  } = useFormContext();

  // CHECK IF ERROR MESSAGE EXISTS
  const errorMessage = getNestedErrorProperty(errors, name)?.message;

  const isDirty = dirtyFields[name];

  return (
    <div className={clsx('flex justify-between w-full items-center gap-3', className)}>
      <div className="max-w-20 capitalize text-xs text-gray-700">{title}</div>
      <div className={clsx('flex gap-4 items-center', optionsClassName)}>
        {options.map((option) => (
          <div className="flex justify-between" key={option.value}>
            <div className="flex items-center self-start gap-2">
              <RadioInput
                {...register(name)}
                id={name + option.value}
                value={option.value}
                disabled={disabled}
                onBlur={onBlur}
                required={required}
              />
              <label htmlFor={name + option.value} className="flex text-xs lowercase text-gray-900">
                {option.label}
              </label>
            </div>
            {option.additionalNode && <span className="flex text-xs text-gray-500 font-medium pr-2 ml-3">{option.additionalNode}</span>}
          </div>
        ))}
        <IcoClose
          className={clsx('size-2.5 text-gray-600 cursor-pointer', isDirty ? 'visible' : 'invisible')}
          onClick={() => resetField(name)}
        />
      </div>

      {/* DISPLAY ERROR MESSAGE */}
      {errorMessage && <div className="mt-2 text-xs text-red-600 flex">{errorMessage}</div>}
    </div>
  );
};

const baseStyle = clsx(
  'appearance-none bg-none',
  'after:relative checked:after:block after:inset-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full',
  'focus:outline-2 focus:outline-offset-0 focus:outline-focus ring-0',
  'disabled:bg-gray-200 disabled:border-gray-400 disabled:after:bg-gray-400',
);
const validStyle = clsx(
  'border-gray-500 bg-white after:bg-blue-600',
  'checked:border-blue-600',
  'enabled:hover:border-blue-700 enabled:hover:after:bg-blue-700',
  'enabled:active:border-blue-900 enabled:active:after:bg-blue-900',
);
const invalidStyle = clsx(
  'border-red-500 bg-white after:bg-red-500',
  'enabled:hover:border-red-700 enabled:hover:after:bg-red-700',
  'enabled:active:border-red-800 enabled:active:after:bg-red-800',
);

const largeStyle = clsx('size-6 after:size-3');
const smallStyle = clsx('size-4 after:size-2');

export type RadioSize = 'L' | 'S';

type RadioInputElementProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

type RadioInputProps = {
  size?: RadioSize;
  hasError?: boolean;
} & RadioInputElementProps;

export const RadioInput = ({ hasError, size = 'L', className, ...rest }: RadioInputProps) => {
  return (
    <input
      type="radio"
      className={clsx(
        className,
        baseStyle,
        {
          L: largeStyle,
          S: smallStyle,
        }[size],
        hasError ? invalidStyle : validStyle,
      )}
      {...rest}
    />
  );
};
